import React, { useState } from "react";
import { cn } from "@/utils/utils";
import { Preferences, PreferencesWithIndication } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { useTranslation } from "react-i18next";
import DisplayPopover, {
  DisplayPopoverProps
} from "@/components/controls/ToolbarActions/DisplayPopover/DisplayPopover";
import { useDisplaySettingsChanged } from "./useDisplaySettingsChanged";

type DisplayPopoverOptions = Omit<
  DisplayPopoverProps,
  "isOpen" | "onOpenChange" | "children"
>;

interface Props extends DisplayPopoverOptions {
  className?: string;
}

const DisplayButton: React.FC<Props> = ({
  className,
  groupBy,
  setGroupBy,
  groupByOptions,
  groupByFilterOptions,
  sorting,
  onSortingChange,
  sortByOptions,
  defaultSortField = "name",
  onReset,
  selectedView,
  columnsState,
  defaultColumnsState,
  lockFirstColumn,
  onColumnsApply,
  tableId,
  customFields,
  groupByFields,
  showTableColumns = true,
  showAsValue,
  onShowAsChange,
  showAsOptions,
  compareTo,
  onCompareToChange,
  defaultCompareTo,
  sortMenuExcludedColumnIds,
  groupByDateBuckets
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { hasChanges } = useDisplaySettingsChanged({
    groupBy,
    sorting,
    defaultSortField,
    compareTo,
    defaultCompareTo,
    columnsState,
    defaultColumnsState
  });

  return (
    <DisplayPopover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      groupBy={groupBy}
      setGroupBy={setGroupBy}
      groupByOptions={groupByOptions}
      groupByFilterOptions={groupByFilterOptions}
      sorting={sorting}
      onSortingChange={onSortingChange}
      sortByOptions={sortByOptions}
      defaultSortField={defaultSortField}
      onReset={onReset}
      selectedView={selectedView}
      columnsState={columnsState}
      defaultColumnsState={defaultColumnsState}
      lockFirstColumn={lockFirstColumn}
      onColumnsApply={onColumnsApply}
      tableId={tableId}
      customFields={customFields}
      groupByFields={groupByFields}
      showTableColumns={showTableColumns}
      showAsValue={showAsValue}
      onShowAsChange={onShowAsChange}
      showAsOptions={showAsOptions}
      compareTo={compareTo}
      onCompareToChange={onCompareToChange}
      defaultCompareTo={defaultCompareTo}
      sortMenuExcludedColumnIds={sortMenuExcludedColumnIds}
      groupByDateBuckets={groupByDateBuckets}
    >
      <Button
        variant="secondaryNoBg"
        className={cn(
          "group gap-1 px-[6px] py-1 border-transparent group cursor-pointer relative",
          isOpen && "bg-surface-focus-selected",
          className
        )}
        data-state={isOpen ? "open" : "closed"}
        data-test="toolbar-display-button"
      >
        <IconContainer
          color="secondary"
          className={cn(
            "group-hover:!text-primary transition-all duration-300 ease-in-out",
            isOpen && "!text-primary"
          )}
        >
          {hasChanges ? <PreferencesWithIndication /> : <Preferences />}
        </IconContainer>
        <Text
          as="label"
          variant="bodySecondary"
          className="text-nowrap cursor-pointer"
        >
          {t("datatoolbar.display.title")}
        </Text>
      </Button>
    </DisplayPopover>
  );
};

export default DisplayButton;
